/**
 * VirtVSwitchesList — Virtual switches (estándar de listados del CMDB).
 *
 * Antes: tabla propia con tema oscuro a mano, los port groups desplegándose
 * dentro de la fila (con un `colSpan` fijo que se rompía al ocultar columnas),
 * diálogo de borrado propio y ficha abriendo en edición.
 *
 * Ahora: `ListPage` con buscador, filtros, columnas, vistas y export; la ficha
 * entra en **lectura**. Los port groups pasan a un **diálogo** propio.
 *
 * Botones propios:
 *  · **Ver los port groups** — diálogo con nombre, VLAN y tipo de cada uno.
 *  · **Ver qué VMs usan sus redes** — diálogo con las NICs de las VMs que
 *    apuntan a sus port groups (endpoint `GET /virt/nics?vswitch_id=`, añadido
 *    porque `GET /vms` sólo daba el número de NICs).
 *  · **Ver el hipervisor o cluster** — a la ficha del elemento que lo aloja.
 *  · **Ver la VLAN en IPAM** — si alguno de sus port groups tiene VLAN y esa
 *    VLAN está dada de alta en IPAM, se abre su ficha allí.
 *  · **Copiar** — precarga el vSwitch con sus port groups, pero sin uplinks.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowsRightLeftIcon,
  ListBulletIcon,
  Squares2X2Icon,
  RectangleGroupIcon,
  ComputerDesktopIcon,
  ServerStackIcon,
  CircleStackIcon,
  GlobeAltIcon,
} from '@heroicons/react/24/outline';
import ListPage from './common/ListPage';
import Dialog from './common/Dialog';
import VirtVSwitchForm from './VirtVSwitchForm';
import { useListView } from '../views/ViewsContext';
import useNavOrder from '../hooks/useNavOrder';
import { getServerFormStyle } from '../utils/serverFormStyle';
import api from '../services/api';

const VSWITCH_TYPE_LABEL = {
  standard:         'Standard vSwitch',
  distributed:      'Distributed Switch (DVS)',
  ovs:              'Open vSwitch (OVS)',
  hyper_v_external: 'Hyper-V External',
  linux_bridge:     'Linux Bridge',
};

const PG_TYPE_META = {
  vm:         { label: 'VM Network', badge: '' },
  vmkernel:   { label: 'VMkernel',   badge: '' },
  management: { label: 'Management', badge: 'list-badge--warn' },
  storage:    { label: 'Storage',    badge: '' },
};

const EMPTY_VSWITCH = {
  name: '', vswitch_type: 'standard', hypervisor_id: '', cluster_id: '',
  uplink_count: 2, mtu: 1500, notes: '',
};

export default function VirtVSwitchesList({ companyId }) {
  const { t } = useTranslation();
  const tr = useCallback((key, fallback) => {
    const value = t(key);
    return !value || value === key ? fallback : value;
  }, [t]);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [items, setItems]         = useState([]);
  const [ipamVlans, setIpamVlans] = useState([]);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [search, setSearch]       = useState('');

  const [filterType, setFilterType]         = useState('');
  const [filterScope, setFilterScope]       = useState('');
  const [filterHypervisor, setFilterHyp]    = useState('');
  const [filterCluster, setFilterCluster]   = useState('');
  const [filterMtu, setFilterMtu]           = useState('');
  const [filterPg, setFilterPg]             = useState('');
  const [filterUplinks, setFilterUplinks]   = useState('');

  const [selected, setSelected]       = useState(null);
  const [initialMode, setInitialMode] = useState('edit');
  const [cloneSource, setCloneSource] = useState(null);
  const [hlId, setHlId]               = useState(null);

  /* Diálogos */
  const [pgsOf, setPgsOf]           = useState(null);
  const [nicsOf, setNicsOf]         = useState(null);
  const [nics, setNics]             = useState([]);
  const [nicsLoading, setNicsLoading] = useState(false);

  /** El nombre del vSwitch, el hypervisor o el cluster llegan como query
      (`?hypervisorId=`, `?clusterId=`, `?tipo=`) desde los listados vecinos. */
  const qHypervisor = searchParams.get('hypervisorId');
  const qCluster    = searchParams.get('clusterId');
  const qTipo       = searchParams.get('tipo');

  useEffect(() => {
    if (qHypervisor) setFilterHyp(String(qHypervisor));
    if (qCluster) setFilterCluster(String(qCluster));
    if (qTipo) setFilterType(qTipo);
  }, [qHypervisor, qCluster, qTipo]);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError('');
    try {
      const [vsw, vlans] = await Promise.all([
        api.getVSwitches(companyId),
        api.getIpamVlans(companyId).catch(() => ({ data: [] })),
      ]);
      setItems(vsw.data || []);
      setIpamVlans(vlans.data || []);
    } catch (e) {
      setError(e.message || 'Error cargando los vSwitches');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  /* ?destacar=<id>[&abrir=1] */
  useEffect(() => {
    const raw = searchParams.get('destacar');
    if (!raw || !items.length) return undefined;
    const id = Number(raw);
    const target = items.find(v => v.id === id);
    if (!target) return undefined;
    const abrir = searchParams.get('abrir') === '1';
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('destacar');
      next.delete('abrir');
      return next;
    }, { replace: true });
    if (abrir) { setCloneSource(null); setInitialMode('view'); setSelected(target); return undefined; }
    setHlId(id);
    const row = document.getElementById(`virt-vsw-row-${id}`);
    if (row) row.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const timer = setTimeout(() => setHlId(null), 4000);
    return () => clearTimeout(timer);
  }, [searchParams, items, setSearchParams]);

  /* ── ficha ──────────────────────────────────────────────────────────────── */
  const openView   = (v) => { setCloneSource(null); setInitialMode('view');   setSelected(v); };
  const openEdit   = (v) => { setCloneSource(null); setInitialMode('edit');   setSelected(v); };
  const openCreate = ()  => { setCloneSource(null); setInitialMode('create'); setSelected({ ...EMPTY_VSWITCH }); };
  /** Copiar: se lleva los port groups, pero no los uplinks (son físicos). */
  const openCopy   = (v) => {
    if (!v) return;
    setSelected(null);
    setCloneSource({
      ...v,
      id: undefined,
      name: v.name ? `${v.name} (copia)` : '',
      uplink_count: 0,
      port_groups: (v.port_groups || []).map(pg => ({ ...pg, id: undefined, vswitch_id: undefined })),
    });
    setInitialMode('create');
  };
  const closeForm = () => { setSelected(null); setCloneSource(null); load(); };

  const handleDelete = async (v) => {
    try {
      await api.deleteVSwitch(v.id, companyId);
      await load();
    } catch (e) {
      setError(e.message || 'Error eliminando el vSwitch');
    }
  };

  /* ── «qué VMs usan estas redes» ─────────────────────────────────────────── */
  const openNics = async (v) => {
    setNicsOf(v);
    setNics([]);
    setNicsLoading(true);
    try {
      const res = await api.getVirtNics(companyId, { vswitch_id: v.id });
      setNics(res.data || []);
    } catch (e) {
      setError(e.message || 'Error cargando las VMs conectadas');
      setNicsOf(null);
    } finally {
      setNicsLoading(false);
    }
  };

  /* ── opciones de filtro (derivadas de los datos) ─────────────────────────── */
  const typeOptions = useMemo(() => {
    const seen = new Map();
    items.forEach(v => { if (v.vswitch_type) seen.set(v.vswitch_type, VSWITCH_TYPE_LABEL[v.vswitch_type] || v.vswitch_type); });
    Object.entries(VSWITCH_TYPE_LABEL).forEach(([value, label]) => { if (!seen.has(value)) seen.set(value, label); });
    return [...seen.entries()].map(([value, label]) => ({ value, label }));
  }, [items]);

  const hypersWithVsw = useMemo(() => {
    const seen = new Map();
    items.forEach(v => { if (v.hypervisor_id) seen.set(String(v.hypervisor_id), v.hypervisor_name || `#${v.hypervisor_id}`); });
    return [...seen.entries()].map(([value, label]) => ({ value, label }));
  }, [items]);

  const clustersWithVsw = useMemo(() => {
    const seen = new Map();
    items.forEach(v => { if (v.cluster_id) seen.set(String(v.cluster_id), v.cluster_name || `#${v.cluster_id}`); });
    return [...seen.entries()].map(([value, label]) => ({ value, label }));
  }, [items]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return items.filter((v) => {
      if (filterType && v.vswitch_type !== filterType) return false;
      if (filterScope === 'hyp' && !v.hypervisor_id) return false;
      if (filterScope === 'cluster' && !v.cluster_id) return false;
      if (filterHypervisor && String(v.hypervisor_id || '') !== String(filterHypervisor)) return false;
      if (filterCluster && String(v.cluster_id || '') !== String(filterCluster)) return false;
      if (filterMtu === 'jumbo' && !(v.mtu >= 9000)) return false;
      if (filterMtu === 'standard' && v.mtu >= 9000) return false;
      const pgCount = (v.port_groups || []).length;
      if (filterPg === 'with' && pgCount === 0) return false;
      if (filterPg === 'without' && pgCount > 0) return false;
      if (filterUplinks === 'with' && !(v.uplink_count > 0)) return false;
      if (filterUplinks === 'without' && v.uplink_count > 0) return false;
      if (!needle) return true;
      return [
        v.name,
        VSWITCH_TYPE_LABEL[v.vswitch_type] || v.vswitch_type,
        v.hypervisor_name, v.cluster_name,
        (v.port_groups || []).map(pg => pg.name).filter(Boolean).join(' '),
      ].filter(Boolean).join(' ').toLowerCase().includes(needle);
    });
  }, [items, search, filterType, filterScope, filterHypervisor, filterCluster, filterMtu, filterPg, filterUplinks]);

  // Orden de las fichas congelado mientras la ficha está abierta.
  const navVSwitches = useNavOrder(filtered, selected, selected !== null);

  /* ── IPAM: VLAN por número ───────────────────────────────────────────────── */
  /** `vlanNumber -> id` de la VLAN en IPAM (para el botón «Ver la VLAN»). */
  const vlanIdByNumber = useMemo(() => {
    const map = new Map();
    ipamVlans.forEach((vl) => {
      if (vl.vlan_id == null) return;
      if (!map.has(vl.vlan_id)) map.set(vl.vlan_id, vl.id);
    });
    return map;
  }, [ipamVlans]);

  const vlanNumbersOf = (v) => [...new Set((v.port_groups || [])
    .map(pg => pg.vlan_id)
    .filter(n => n != null && n !== 0))];

  /* ── columnas ───────────────────────────────────────────────────────────── */
  const columns = useMemo(() => {
    const dash = <span className="list-hint">—</span>;
    return [
      {
        key: 'name',
        label: tr('virt.name', 'Nombre'),
        sortable: true,
        render: (v) => (
          <span style={{ display: 'inline-flex', flexDirection: 'column' }}>
            <span style={{ fontWeight: 600 }}>{v.name}</span>
            <span className="list-hint">{VSWITCH_TYPE_LABEL[v.vswitch_type] || v.vswitch_type}</span>
          </span>
        ),
      },
      {
        key: 'type',
        label: tr('virt.type', 'Tipo'),
        sortable: true,
        sortValue: (v) => v.vswitch_type || '',
        render: (v) => (
          <span className="list-badge">{VSWITCH_TYPE_LABEL[v.vswitch_type] || v.vswitch_type || dash}</span>
        ),
      },
      {
        key: 'scope',
        label: tr('virt.scope', 'Alcance'),
        sortable: true,
        sortValue: (v) => v.cluster_name || v.hypervisor_name || '',
        render: (v) => {
          const name = v.cluster_name || v.hypervisor_name;
          if (!name) return dash;
          return (
            <span style={{ display: 'inline-flex', flexDirection: 'column' }}>
              <span>{name}</span>
              <span className="list-hint">
                {v.cluster_name ? tr('virt.scope_cluster', 'Cluster') : tr('virt.scope_hypervisor', 'Hipervisor')}
              </span>
            </span>
          );
        },
      },
      {
        key: 'uplink_count',
        label: tr('virt.uplinks', 'Uplinks'),
        sortable: true,
        sortValue: (v) => v.uplink_count ?? 0,
        render: (v) => (v.uplink_count != null
          ? <span className="list-badge">{v.uplink_count}×</span>
          : dash),
      },
      {
        key: 'mtu',
        label: 'MTU',
        sortable: true,
        sortValue: (v) => v.mtu ?? 1500,
        render: (v) => {
          const mtu = v.mtu || 1500;
          return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span className="list-code">{mtu}</span>
              {mtu >= 9000 && <span className="list-badge">{tr('virt.jumbo', 'Jumbo')}</span>}
            </span>
          );
        },
      },
      {
        key: 'port_groups',
        label: tr('virt.port_groups', 'Port groups'),
        sortable: true,
        sortValue: (v) => (v.port_groups || []).length,
        render: (v) => {
          const pgs = v.port_groups || [];
          if (!pgs.length) return <span className="list-hint">{tr('virt.no_pgs', 'Sin port groups')}</span>;
          return (
            <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
              {pgs.slice(0, 3).map((pg) => (
                <span
                  key={pg.id}
                  className={`list-badge ${(PG_TYPE_META[pg.pg_type] || {}).badge || ''}`.trim()}
                  title={`${pg.name}${pg.vlan_id ? ` · VLAN ${pg.vlan_id}` : ''} · ${(PG_TYPE_META[pg.pg_type] || {}).label || pg.pg_type}`}
                >
                  {pg.name}
                  {pg.vlan_id ? ` (${pg.vlan_id})` : ''}
                </span>
              ))}
              {pgs.length > 3 && (
                <button
                  type="button"
                  className="list-badge"
                  style={{ cursor: 'pointer' }}
                  onClick={(event) => { event.stopPropagation(); setPgsOf(v); }}
                  title={tr('virt.view_pgs', 'Ver todos los port groups')}
                >
                  +{pgs.length - 3}
                </button>
              )}
            </span>
          );
        },
      },
      {
        key: 'notes',
        label: tr('description', 'Notas'),
        render: (v) => v.notes || dash,
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, tr]);

  const defaultVisible = ['name', 'type', 'scope', 'uplink_count', 'mtu', 'port_groups'];

  const filters = [
    {
      key: 'type', label: tr('virt.type', 'Tipo'), allLabel: tr('virt.type_all', 'Todos los tipos'),
      value: filterType, onChange: setFilterType, options: typeOptions,
    },
    {
      key: 'scope', label: tr('virt.scope', 'Alcance'), allLabel: tr('virt.scope_all', 'Cualquier alcance'),
      value: filterScope, onChange: setFilterScope,
      options: [
        { value: 'hyp', label: tr('virt.scope_hypervisor', 'Hipervisor') },
        { value: 'cluster', label: tr('virt.scope_cluster', 'Cluster (distribuido)') },
      ],
    },
    {
      key: 'hypervisor', label: tr('virt.hypervisor', 'Hipervisor'), allLabel: tr('virt.hyp_all', 'Cualquier hipervisor'),
      value: filterHypervisor, onChange: setFilterHyp, options: hypersWithVsw,
    },
    {
      key: 'cluster', label: tr('virt.cluster', 'Cluster'), allLabel: tr('virt.cluster_all', 'Cualquier cluster'),
      value: filterCluster, onChange: setFilterCluster, options: clustersWithVsw,
    },
    {
      key: 'mtu', label: 'MTU', allLabel: tr('virt.mtu_all', 'Cualquier MTU'),
      value: filterMtu, onChange: setFilterMtu,
      options: [
        { value: 'jumbo', label: tr('virt.mtu_jumbo', 'Jumbo (9000)') },
        { value: 'standard', label: tr('virt.mtu_standard', 'Estándar (1500)') },
      ],
    },
    {
      key: 'pgs', label: tr('virt.port_groups', 'Port groups'), allLabel: tr('virt.pgs_all', 'Con o sin port groups'),
      value: filterPg, onChange: setFilterPg,
      options: [
        { value: 'with', label: tr('virt.pgs_with', 'Con port groups') },
        { value: 'without', label: tr('virt.pgs_without', 'Sin port groups') },
      ],
    },
    {
      key: 'uplinks', label: tr('virt.uplinks', 'Uplinks'), allLabel: tr('virt.uplinks_all', 'Con o sin uplinks'),
      value: filterUplinks, onChange: setFilterUplinks,
      options: [
        { value: 'with', label: tr('virt.uplinks_with', 'Con uplinks') },
        { value: 'without', label: tr('virt.uplinks_without', 'Sin uplinks (aislado)') },
      ],
    },
  ];

  const totalPgs = items.reduce((acc, v) => acc + (v.port_groups?.length || 0), 0);
  const subtitle = `${items.length} ${tr('virt.vsw_plural', 'vSwitches')} · ${totalPgs} port groups`
    + ` · ${filtered.length} ${tr('location.filtered', 'filtrados')}`;

  const exportRows = useMemo(() => filtered.map(v => ({
    'Nombre': v.name || '',
    'Tipo': VSWITCH_TYPE_LABEL[v.vswitch_type] || v.vswitch_type || '',
    'Alcance': v.cluster_name ? 'Cluster' : 'Hipervisor',
    'Hipervisor': v.hypervisor_name || '',
    'Cluster': v.cluster_name || '',
    'Uplinks': v.uplink_count ?? '',
    'MTU': v.mtu || 1500,
    'Nº port groups': (v.port_groups || []).length,
    'Port groups': (v.port_groups || []).map(pg => pg.name).filter(Boolean).join(' · '),
    'VLANs': vlanNumbersOf(v).join(', '),
    'Notas': v.notes || '',
  })), [filtered]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Tarjetas: lo esencial del vSwitch, con los mismos botones de fila. */
  function renderCards({ rows, rowActions }) {
    return (
      <div className="list-cards">
        {rows.map((v) => (
          <div
            key={v.id}
            id={`virt-vsw-card-${v.id}`}
            className={`list-card${v.id === hlId ? ' list-row-highlight' : ''}`}
            role="button"
            tabIndex={0}
            onClick={() => openView(v)}
            onKeyDown={(event) => { if (event.key === 'Enter') openView(v); }}
            title={`${tr('action.view', 'Ver')} ${v.name}`}
          >
            <div className="list-card-head">
              <div>
                <div className="list-card-title">{v.name}</div>
                <span className="list-hint">{VSWITCH_TYPE_LABEL[v.vswitch_type] || v.vswitch_type}</span>
              </div>
            </div>

            <div className="list-card-meta">
              {(v.cluster_name || v.hypervisor_name) && <span>{v.cluster_name || v.hypervisor_name}</span>}
              <span className="list-badge">{v.uplink_count ?? 0} uplinks</span>
              <span className="list-badge">MTU {v.mtu || 1500}</span>
              <span className="list-badge">{(v.port_groups || []).length} port groups</span>
            </div>

            <div className="list-card-actions">{rowActions(v)}</div>
          </div>
        ))}
      </div>
    );
  }

  const VSW_VIEWS = useMemo(() => [
    { key: 'list', label: tr('ipam.view_list', 'Lista'), icon: ListBulletIcon, title: tr('ipam.view_list_hint', 'Tabla con todas las columnas') },
    { key: 'card', label: tr('ipam.view_cards', 'Fichas'), icon: Squares2X2Icon, title: tr('ipam.view_cards_hint', 'Tarjetas con lo esencial de cada vSwitch'), render: renderCards },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t, hlId]);
  useListView('virt_vswitches', 'list');

  if (!companyId) {
    return (
      <div className="list-page" style={{ padding: '1.5rem' }}>
        <div className="alert alert-warning">
          {tr('virt.select_company', 'Selecciona una empresa para ver la virtualización.')}
        </div>
      </div>
    );
  }

  /** Limpia los filtros que llegan por query (los chips de vuelta a «todo»). */
  const clearQueryFilter = (which) => {
    if (which === 'hypervisor') setFilterHyp('');
    if (which === 'cluster') setFilterCluster('');
    if (which === 'type') setFilterType('');
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (which === 'hypervisor') next.delete('hypervisorId');
      if (which === 'cluster') next.delete('clusterId');
      if (which === 'type') next.delete('tipo');
      return next;
    }, { replace: true });
  };

  return (
    <>
      {(selected !== null || cloneSource) && (
        <VirtVSwitchForm
          vswitch={cloneSource || (selected && selected.id ? selected : null)}
          companyId={companyId}
          initialMode={cloneSource ? 'create' : initialMode}
          variant={getServerFormStyle() === 'cards' ? 'cards' : 'classic'}
          navItems={navVSwitches}
          navIndex={!cloneSource && selected && selected.id ? navVSwitches.findIndex(v => String(v.id) === String(selected.id)) : -1}
          onNavigate={(destino) => setSelected(destino)}
          onClose={closeForm}
          onSave={(creado) => {
            load();
            // Alta: el listado adopta el registro creado y la ficha sigue abierta.
            if (creado?.id) { setInitialMode('edit'); setSelected(creado); }
          }}
        />
      )}

      <div style={{ display: (selected !== null || cloneSource) ? 'none' : undefined }}>
        <ListPage
          icon={ArrowsRightLeftIcon}
          title={tr('virt.vsw_title', 'Virtual switches')}
          subtitle={subtitle}

          onCreate={openCreate}
          createLabel={tr('virt.new_vsw', 'Nuevo vSwitch')}
          createTitle={tr('virt.new_vsw_hint', 'Red virtual de un hipervisor o distribuida de un cluster')}

          search={search}
          onSearchChange={setSearch}
          searchPlaceholder={tr('virt.vsw_search', 'Buscar por nombre, tipo, hipervisor, cluster…')}
          filters={filters}

          exportRows={exportRows}
          exportFileBase="vswitches"
          exportSheetName="vSwitches"

          columns={columns}
          columnStorageKey="cmdb_cols_virt_vswitches"
          companyId={companyId}
          defaultVisible={defaultVisible}
          rows={filtered}
          rowKey={(v) => v.id}
          rowId={(v) => `virt-vsw-row-${v.id}`}
          rowClassName={(v) => (v.id === hlId ? 'list-row-highlight' : '')}
          onRowClick={openView}
          rowTitle={(v) => `${tr('action.view', 'Ver')} ${v.name}`}

          headerActions={(
            <>
              {qHypervisor && (
                <button
                  type="button"
                  className="list-btn-secondary"
                  onClick={() => clearQueryFilter('hypervisor')}
                  title={tr('virt.clear_filter', 'Quitar este filtro')}
                >
                  × {tr('virt.filtered_by_hypervisor', 'Solo este hipervisor')}
                </button>
              )}
              {qCluster && (
                <button
                  type="button"
                  className="list-btn-secondary"
                  onClick={() => clearQueryFilter('cluster')}
                  title={tr('virt.clear_filter', 'Quitar este filtro')}
                >
                  × {tr('virt.filtered_by_cluster', 'Solo este cluster')}
                </button>
              )}
              {qTipo && (
                <button
                  type="button"
                  className="list-btn-secondary"
                  onClick={() => clearQueryFilter('type')}
                  title={tr('virt.clear_filter', 'Quitar este filtro')}
                >
                  × {VSWITCH_TYPE_LABEL[qTipo] || qTipo}
                </button>
              )}
            </>
          )}

          views={VSW_VIEWS}
          listKey="virt_vswitches"
          defaultView="list"

          loading={loading}
          loadingText={tr('msg.loading', 'Cargando vSwitches…')}
          error={error}

          empty={{
            icon: '🔀',
            title: items.length === 0
              ? tr('virt.vsw_empty', 'No hay vSwitches')
              : tr('msg.no_results', 'Ningún vSwitch coincide con los filtros'),
            subtitle: items.length === 0
              ? tr('virt.vsw_empty_hint', 'Crea los switches virtuales de tus hipervisores y clusters para poder asignar redes a las VMs.')
              : undefined,
            action: items.length === 0 ? (
              <button type="button" className="list-btn-primary" onClick={openCreate}>
                ＋ {tr('virt.new_vsw', 'Nuevo vSwitch')}
              </button>
            ) : undefined,
          }}

          actions={{
            extra: (v) => {
              const pgCount = (v.port_groups || []).length;
              const vlan = vlanNumbersOf(v).find(n => vlanIdByNumber.has(n));
              const scopePath = v.cluster_id
                ? `/virt/clusters?destacar=${v.cluster_id}&abrir=1`
                : (v.hypervisor_id ? `/virt/hypervisors?destacar=${v.hypervisor_id}&abrir=1` : null);
              return (
                <>
                  {/* Ver los port groups */}
                  <button
                    type="button"
                    className="list-action-btn"
                    disabled={!pgCount}
                    onClick={() => setPgsOf(v)}
                    title={tr('virt.view_pgs', 'Ver todos los port groups')}
                  >
                    <RectangleGroupIcon />
                  </button>
                  {/* Ver qué VMs usan sus redes */}
                  <button
                    type="button"
                    className="list-action-btn"
                    disabled={!pgCount}
                    onClick={() => openNics(v)}
                    title={tr('virt.view_users', 'Ver qué VMs usan sus redes')}
                  >
                    <ComputerDesktopIcon />
                  </button>
                  {/* Ver el hipervisor o cluster al que pertenece */}
                  <button
                    type="button"
                    className="list-action-btn"
                    disabled={!scopePath}
                    onClick={() => scopePath && navigate(scopePath)}
                    title={v.cluster_name
                      ? `${tr('virt.view_cluster', 'Ver el cluster al que pertenece')}: ${v.cluster_name}`
                      : (v.hypervisor_name
                        ? `${tr('virt.view_hypervisor', 'Ver el hipervisor')}: ${v.hypervisor_name}`
                        : tr('virt.no_scope', 'Sin hipervisor ni cluster asignado'))}
                  >
                    {v.cluster_id ? <CircleStackIcon /> : <ServerStackIcon />}
                  </button>
                  {/* Ver la VLAN en IPAM */}
                  <button
                    type="button"
                    className="list-action-btn"
                    disabled={!vlan}
                    onClick={() => vlan && navigate(`/networks/vlans?destacar=${vlanIdByNumber.get(vlan)}`)}
                    title={vlan
                      ? `${tr('virt.view_vlan', 'Ver la VLAN en IPAM')}: VLAN ${vlan}`
                      : tr('virt.no_ipam_vlan', 'Sus redes no usan ninguna VLAN dada de alta en IPAM')}
                  >
                    <GlobeAltIcon />
                  </button>
                </>
              );
            },
            onCopy: openCopy,
            onEdit: openEdit,
            onDelete: handleDelete,
            deleteTitle: tr('virt.vsw_delete_title', 'Eliminar vSwitch'),
            deleteMessage: (v) => (
              <>
                {tr('virt.delete_confirm', '¿Seguro que quieres eliminar')} <strong>{v.name}</strong>?{' '}
                {(v.port_groups || []).length > 0
                  ? tr('virt.vsw_delete_used', `Se eliminarán también sus ${(v.port_groups || []).length} port group(s).`)
                  : tr('ip.delete_warn', 'No se puede deshacer.')}
              </>
            ),
          }}
        />
      </div>

      {/* ══ Diálogo: port groups ══ */}
      {pgsOf && (
        <Dialog
          title={`${tr('virt.port_groups', 'Port groups')} · ${pgsOf.name}`}
          icon={<RectangleGroupIcon style={{ width: 18, height: 18 }} />}
          maxWidth={640}
          onClose={() => setPgsOf(null)}
          footer={(
            <button type="button" className="list-btn-secondary" onClick={() => setPgsOf(null)}>
              {tr('action.close', 'Cerrar')}
            </button>
          )}
        >
          <table className="table table-sm table-hover align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th>{tr('virt.name', 'Nombre')}</th>
                <th>VLAN</th>
                <th>{tr('virt.pg_type', 'Tipo')}</th>
                <th>{tr('description', 'Notas')}</th>
              </tr>
            </thead>
            <tbody>
              {(pgsOf.port_groups || []).map((pg) => {
                const meta = PG_TYPE_META[pg.pg_type] || { label: pg.pg_type };
                const vlanKnown = pg.vlan_id && vlanIdByNumber.has(pg.vlan_id);
                return (
                  <tr key={pg.id}>
                    <td style={{ fontWeight: 600 }}>{pg.name}</td>
                    <td>
                      {pg.vlan_id
                        ? (
                          <button
                            type="button"
                            className="list-badge"
                            style={{ cursor: vlanKnown ? 'pointer' : 'default' }}
                            disabled={!vlanKnown}
                            onClick={() => vlanKnown && navigate(`/networks/vlans?destacar=${vlanIdByNumber.get(pg.vlan_id)}`)}
                            title={vlanKnown
                              ? tr('virt.view_vlan', 'Ver la VLAN en IPAM')
                              : tr('virt.no_ipam_vlan', 'Esta VLAN no está dada de alta en IPAM')}
                          >
                            VLAN {pg.vlan_id}
                          </button>
                        )
                        : <span className="list-hint">{tr('virt.untagged', 'Sin tag')}</span>}
                    </td>
                    <td><span className={`list-badge ${meta.badge || ''}`.trim()}>{meta.label}</span></td>
                    <td>{pg.notes || <span className="list-hint">—</span>}</td>
                  </tr>
                );
              })}
              {(pgsOf.port_groups || []).length === 0 && (
                <tr>
                  <td colSpan={4} className="list-hint" style={{ textAlign: 'center' }}>
                    {tr('virt.no_pgs', 'Sin port groups')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </Dialog>
      )}

      {/* ══ Diálogo: qué VMs usan sus redes ══ */}
      {nicsOf && (
        <Dialog
          title={`${tr('virt.vms_using', 'VMs que usan estas redes')} · ${nicsOf.name}`}
          icon={<ComputerDesktopIcon style={{ width: 18, height: 18 }} />}
          maxWidth={720}
          onClose={() => setNicsOf(null)}
          footer={(
            <button type="button" className="list-btn-secondary" onClick={() => setNicsOf(null)}>
              {tr('action.close', 'Cerrar')}
            </button>
          )}
        >
          {nicsLoading && <p style={{ margin: 0 }}>{tr('msg.loading', 'Cargando…')}</p>}
          {!nicsLoading && nics.length === 0 && (
            <p style={{ margin: 0 }} className="list-hint">
              {tr('virt.no_vms_using', 'Ninguna VM tiene una NIC en los port groups de este vSwitch.')}
            </p>
          )}
          {!nicsLoading && nics.length > 0 && (
            <table className="table table-sm table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>{tr('virt.vm', 'Máquina virtual')}</th>
                  <th>NIC</th>
                  <th>{tr('virt.pg_short', 'Port group')}</th>
                  <th>VLAN</th>
                  <th>MAC</th>
                </tr>
              </thead>
              <tbody>
                {nics.map((n) => (
                  <tr key={n.id}>
                    <td>
                      <button
                        type="button"
                        className="list-badge"
                        style={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/virt/vms?destacar=${n.vm_id}&abrir=1`)}
                        title={tr('virt.view_vm', 'Ver la máquina virtual')}
                      >
                        {n.vm_name || `#${n.vm_id}`}
                      </button>
                    </td>
                    <td>{n.nic_label || <span className="list-hint">—</span>}</td>
                    <td>{n.port_group_name || <span className="list-hint">—</span>}</td>
                    <td>{n.vlan_id ? <span className="list-code">{n.vlan_id}</span> : <span className="list-hint">—</span>}</td>
                    <td>{n.mac_address ? <span className="list-code">{n.mac_address}</span> : <span className="list-hint">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Dialog>
      )}
    </>
  );
}
